import { Dropdown, Menu } from 'antd';
import Button from '../../common/Button';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { MoreVertical } from 'react-feather';
import { useHistory, useRouteMatch } from 'react-router-dom';
import usePermissionsForType, {
  ExportAssetType,
} from '../util/useAddPermissions';
import {
  useAssetConfig,
  useAssetParams,
  useUserinfo,
} from '@mss/mss-component-library';
import { sanitizedUrl } from '../util/sanitize';
import { ExportAssetsModal } from './ExportAssetsModal';

type Props = {
  type: ExportAssetType;
  count: number | undefined;
};

const MoreActionsMenu = ({ type, count }: Props) => {
  const { t } = useTranslation(['asset', 'common']);
  const [hasPermission] = useUserinfo();
  const { push } = useHistory();
  const { url } = useRouteMatch();
  const addPermissions = usePermissionsForType(type, 'add');
  const changePermissions = usePermissionsForType(type, 'change');
  const deletePermissions = usePermissionsForType(type, 'delete');
  const { category } = useAssetParams();
  const { config } = useAssetConfig();
  const [modalOpen, setModalOpen] = React.useState(false);

  function openModal() {
    setModalOpen(!!count);
  }

  function closeModal() {
    setModalOpen(false);
  }

  return (
    <>
      <Dropdown
        trigger={['click']}
        placement="bottomRight"
        arrow
        disabled={!count && !hasPermission(addPermissions)}
        overlay={
          <Menu>
            {type !== 'field' && (
              <Menu.Item
                className="px-8 py-2"
                key={'import'}
                disabled={!hasPermission(addPermissions)}
                onClick={() => push(`${sanitizedUrl(url)}/import`)}
              >
                {t(`Import ${category}`)}
              </Menu.Item>
            )}
            {config.discovery &&
              hasPermission(
                config.discovery.permissions.map(x => `view_${x}`)
              ) && (
                <Menu.Item
                  className="px-8 py-2"
                  key={'discovery'}
                  onClick={() => push(`${sanitizedUrl(url)}/discovery`)}
                >
                  {t('Asset discovery')}
                </Menu.Item>
              )}
            {hasPermission('view_automationmonitorprofile') && (
              <Menu.Item
                className="px-8 py-2"
                key={'monitor-profile'}
                onClick={() => push(`${sanitizedUrl(url)}/monitor-profiles`)}
              >
                {t('Manage monitor profiles')}
              </Menu.Item>
            )}
            <Menu.Item
              disabled={!count}
              className="px-8 py-2"
              key={'export-assets-to-csv'}
              onClick={openModal}
            >
              {t('Export assets to CSV')}
            </Menu.Item>
            {hasPermission(changePermissions) && (
              <Menu.Item
                disabled={!count}
                className="px-8 py-2"
                key={'bulk-edit'}
                onClick={() => push(`${sanitizedUrl(url)}/bulk-edit`)}
              >
                {t('Bulk Edit')}
              </Menu.Item>
            )}
            {hasPermission(deletePermissions) && (
              <Menu.Item
                disabled={!count}
                className="px-8 py-2"
                key={'bulk-delete'}
                onClick={() => push(`${sanitizedUrl(url)}/bulk-delete`)}
              >
                {t('Bulk Delete')}
              </Menu.Item>
            )}
          </Menu>
        }
      >
        <Button className="border-0 bg-transparent focus:text-primary hover:text-primary shadow-none">
          <MoreVertical size={20} />
        </Button>
      </Dropdown>
      {modalOpen && (
        <ExportAssetsModal type={type} open={modalOpen} onCancel={closeModal} />
      )}
    </>
  );
};

export default MoreActionsMenu;
